import React, { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../utils/apiHelper';
import { QRCodeCanvas } from 'qrcode.react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';

// Reusable GiftCardPreview component
const GiftCardPreview = ({ giftCard }) => {
  return (
    <div className="w-[350px] h-[600px] bg-gradient-to-b from-pink-200 via-pink-100 to-white rounded-2xl shadow-xl p-4 flex flex-col items-center justify-evenly border-4 border-pink-300">
      <h1 className="text-pink-800 text-3xl font-extrabold text-center mt-2">Gift Card</h1>
      <p className="text-pink-700 text-lg mt-1 font-semibold">Narella Nail Art</p>
      <p className="italic text-pink-700 text-center max-w-[80%]">
        “Porque te mereces lucir espectacular”
      </p>
      <div className="bg-white bg-opacity-70 rounded-lg px-6 py-2 mt-2 shadow-md">
        <p className="text-pink-900 font-bold text-2xl">
          Monto: ${giftCard.initial_value}
        </p>
      </div>
      <div className="text-center mt-2">
        <p className="text-pink-900 font-medium">
          <span className="font-bold">De:</span>{' '}
          {giftCard.cliente_nombre
            ? `${giftCard.cliente_nombre} ${giftCard.cliente_apellido}`
            : 'Mariana Nistal'}
        </p>
        <p className="text-pink-900 font-medium">
          <span className="font-bold">Para:</span> ¡Tu próxima visita!
        </p>
      </div>
      <div className="mt-2 border border-pink-300 p-3 rounded-lg bg-white bg-opacity-70 shadow-inner flex flex-col items-center">
        {(() => {
          const baseUrl = "https://narella.nistal.net/gifts/cards/data";
          const detailUrl = `${baseUrl}/${giftCard.public_code}`;
          return (
            <>
              <QRCodeCanvas
                value={detailUrl}
                size={140}
                bgColor="#ffffff"
                fgColor="#d63384"
                level="H"
                includeMargin={true}
              />
              <p className="text-xs text-pink-700 mt-2">Escanea para ver detalles</p>
            </>
          );
        })()}
      </div>
      <p className="text-pink-800 font-semibold mt-2">
        Cód. Gift Card: {giftCard.public_code}
      </p>
      {giftCard.expires_at && (
        <p className="text-pink-800 text-sm">
          <span className="font-bold">Válido hasta:</span>{' '}
          {new Date(giftCard.expires_at).toLocaleDateString()}
        </p>
      )}
      <p className="text-pink-700 text-sm text-center max-w-[80%] mt-2 italic">
        “Válida para un solo uso. ¡Gracias por elegirnos!”
      </p>
    </div>
  );
};

const GiftCardList = ({ openQRModal, refreshList }) => {
  const [giftCards, setGiftCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '2025-01-01',
    endDate: '2025-03-31',
    state: 'all',
    clientName: '', // Nuevo filtro por nombre
  });
  
  // State to hold the gift card to preview for download
  const [previewGiftCard, setPreviewGiftCard] = useState(null);
  const previewRef = useRef(null);

  useEffect(() => {
    async function fetchGiftCards() {
      setIsLoading(true);
      setError('');
      try {
        let query = `/giftcards?start_date=${filters.startDate}&end_date=${filters.endDate}`;
        const data = await fetchWithAuth(query);
        setGiftCards(data);
      } catch (err) {
        setError('Error al cargar gift cards: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    }
    fetchGiftCards();
  }, [refreshList, filters.startDate, filters.endDate]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Error copiando código:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Download function for a single gift card
  const handleDownloadCard = async (giftCard) => {
    setPreviewGiftCard(giftCard);
    setTimeout(async () => {
      if (!previewRef.current) return;
      try {
        const canvas = await html2canvas(previewRef.current, { scale: 2 });
        const dataURL = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `GiftCard-${giftCard.public_code}.png`;
        link.href = dataURL;
        link.click();
      } catch (err) {
        console.error('Error generating image:', err);
      } finally {
        setPreviewGiftCard(null);
      }
    }, 100);
  };

  // Filtrar gift cards por estado y por nombre de cliente (nombre y apellido)
  const filteredGiftCards = giftCards.filter((card) => {
    const stateMatches = filters.state === 'all' || card.state === filters.state;
    const clientFilter = filters.clientName.toLowerCase();
    const fullName = `${card.cliente_nombre || ''} ${card.cliente_apellido || ''}`.toLowerCase();
    const clientMatches = fullName.includes(clientFilter);
    return stateMatches && clientMatches;
  });

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-lg rounded-lg p-6 space-y-6">
        <h2 className="text-2xl font-bold text-center text-pink-700">Listado de Gift Cards</h2>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700">Desde</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full border-pink-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hasta</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full border-pink-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Estado</label>
            <select
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
              className="mt-1 block w-full border-pink-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="all">Todos</option>
              <option value="active">Activo</option>
              <option value="redeemed">Canjeado</option>
              <option value="expired">Expirado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Cliente</label>
            <input
              type="text"
              name="clientName"
              value={filters.clientName}
              onChange={handleFilterChange}
              placeholder="Buscar cliente"
              className="mt-1 block w-full border-pink-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-100 text-red-700 rounded-lg text-sm font-medium">
            {error}
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="flex justify-center items-center">
            <svg className="animate-spin h-8 w-8 text-pink-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          </div>
        ) : filteredGiftCards.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-pink-200 shadow-inner bg-white/80 backdrop-blur-md">
            <table className="min-w-full w-full table-auto text-sm text-gray-700">

              <thead className="bg-pink-200 bg-opacity-70 text-pink-800 uppercase text-xs font-semibold sticky top-0 shadow-sm z-10">
                <tr>
                  <th className="px-5 py-4 text-left">Código Corto</th>
                  <th className="px-5 py-4 text-left">Cliente</th>
                  <th className="px-5 py-4 text-left">Valor Inicial</th>
                  <th className="px-5 py-4 text-left">Saldo Actual</th>
                  <th className="px-5 py-4 text-left">Estado</th>
                  <th className="px-5 py-4 text-left">Emitida</th>
                  <th className="px-5 py-4 text-left">Expira</th>
                  <th className="px-5 py-4 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-pink-100">
                {filteredGiftCards.map((card) => (
                  <tr key={card.gift_card_id} className="hover:bg-pink-50 transition-all duration-200 ease-in-out group">
                    <td
                      className="px-5 py-4 cursor-pointer font-mono text-sm text-pink-700 relative"
                      onClick={() => copyCode(card.public_code)}
                    >
                      {card.public_code}
                      {copiedCode === card.public_code && (
                        <span className="absolute -top-2 -right-3 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full shadow animate-pulse">
                          Copiado!
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-semibold text-gray-800">
                        {card.cliente_nombre} {card.cliente_apellido}
                      </div>
                      <div className="text-xs text-gray-500">{card.email}</div>
                      <div className="text-xs text-gray-500">{card.telefono}</div>
                    </td>
                    <td className="px-5 py-4 font-medium">${card.initial_value.toFixed(2)}</td>
                    <td className="px-5 py-4 font-medium">${card.current_balance.toFixed(2)}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold tracking-wide ${
                        card.state === 'active'
                          ? 'bg-green-100 text-green-700'
                          : card.state === 'redeemed'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        {card.state}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-700">{format(new Date(card.issued_at), 'yyyy-MM-dd')}</td>
                    <td className="px-5 py-4 text-gray-700">{format(new Date(card.expires_at), 'yyyy-MM-dd')}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => handleDownloadCard(card)}
                        className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-1.5 px-4 rounded-full text-xs transition-all duration-200 shadow-sm group-hover:scale-105"
                      >
                        Descargar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        ) : (
          <p className="text-center text-pink-600 text-sm font-medium">
            No se encontraron gift cards.
          </p>
        )}
      </div>
      {/* Hidden preview container */}
      {previewGiftCard && (
        <div
          ref={previewRef}
          style={{ position: 'absolute', left: '-10000px', top: 0 }}
        >
          <GiftCardPreview giftCard={previewGiftCard} />
        </div>
      )}
    </div>
  );
};

export default GiftCardList;
